import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Form, Button, Badge } from "react-bootstrap";
import { PageHeading, CustomCard } from "../common-styling";
import TableLoader from "../loader/table-loader";
import { showError, showSuccess } from "../../services/toast-service";
import {
  fetchFormDefaults,
  updateFormDefaults
} from "../../services/api-declaration";
import { generateDateTime } from "../../services/common-service";
import { hasAccess } from "../../services/access";
import { ACCESS_CONFIG, SPLIT_TEST_TABS } from "../strings";
import VariantPreview from "../split-tests/variant-preview";

const HARD_CODED = {
  submitButtonBackground: "#e15d3a",
  formHeaderBackground: "#198754"
};

// Site Settings → "Formular-Standards": the global defaults of the classic
// order form per funnel type (split-test elements v2). Set by roll-outs or
// manually here (SUPER_ADMIN); funnels follow them unless they carry their
// own value.
const FormDefaults = () => {
  const { t } = useTranslation();
  const canEdit = hasAccess(ACCESS_CONFIG.ONLY_SUPER_ADMIN);
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [forms, setForms] = useState({});

  const load = useCallback(async () => {
    try {
      const resp = await fetchFormDefaults();
      const data = resp.data.data;
      setDefaults(data);
      setForms({ lead: data.lead, employee: data.employee });
    } catch (error) {
      showError(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, path, value) =>
    setForms((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let obj = next[key];
      for (let i = 0; i < parts.length - 1; i += 1) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = value;
      return next;
    });

  const save = async (key) => {
    setBusy(true);
    try {
      const funnelType =
        key === "employee" ? SPLIT_TEST_TABS.EMPLOYEE : SPLIT_TEST_TABS.LEAD;
      const f = forms[key];
      await updateFormDefaults({
        funnelType,
        submitButtonBackground: f.submitButtonBackground || "",
        formHeaderBackground: f.formHeaderBackground || "",
        socialProof: {
          enabled: f.socialProof.enabled,
          template: f.socialProof.template,
          minCount: Number(f.socialProof.minCount),
          windowDays: Number(f.socialProof.windowDays)
        },
        googleRating: {
          enabled: f.googleRating.enabled,
          minRating: Number(f.googleRating.minRating),
          minCount: Number(f.googleRating.minCount)
        }
      });
      showSuccess(t("formDefaults.saved"));
      await load();
    } catch (error) {
      showError(error?.response?.data?.message || error.message);
    } finally {
      setBusy(false);
    }
  };

  const renderBlock = (key, label) => {
    const f = forms[key];
    const stored = defaults?.[key];
    if (!f) return null;
    const funnelType = key === "employee" ? "EMPLOYEE" : "LEAD";
    return (
      <Col lg={6} key={key}>
        <CustomCard className="p-3 mb-3 h-100">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-bold fs-5">{label}</div>
            {stored?.updatedAt && (
              <Badge bg="light" text="dark">
                {t("formDefaults.lastSet", {
                  date: generateDateTime(stored.updatedAt)
                })}
                {stored.setBySplitTest
                  ? ` · ${t("formDefaults.bySplitTest")}`
                  : ""}
              </Badge>
            )}
          </div>
          <Row className="g-3">
            <Col md={7}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t("formDefaults.submitButtonBackground")}
                </Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    style={{ width: 48 }}
                    value={
                      f.submitButtonBackground ||
                      HARD_CODED.submitButtonBackground
                    }
                    disabled={!canEdit}
                    onChange={(e) =>
                      setField(key, "submitButtonBackground", e.target.value)
                    }
                  />
                  <Form.Control
                    placeholder={`${t("formDefaults.hardCoded")} ${HARD_CODED.submitButtonBackground}`}
                    value={f.submitButtonBackground || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setField(key, "submitButtonBackground", e.target.value)
                    }
                  />
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t("formDefaults.formHeaderBackground")}
                </Form.Label>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    type="color"
                    style={{ width: 48 }}
                    value={
                      f.formHeaderBackground || HARD_CODED.formHeaderBackground
                    }
                    disabled={!canEdit}
                    onChange={(e) =>
                      setField(key, "formHeaderBackground", e.target.value)
                    }
                  />
                  <Form.Control
                    placeholder={`${t("formDefaults.hardCoded")} ${HARD_CODED.formHeaderBackground}`}
                    value={f.formHeaderBackground || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setField(key, "formHeaderBackground", e.target.value)
                    }
                  />
                </div>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Check
                  type="switch"
                  id={`${key}-socialProof`}
                  label={t("formDefaults.socialProof")}
                  checked={Boolean(f.socialProof.enabled)}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setField(key, "socialProof.enabled", e.target.checked)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={f.socialProof.template || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setField(key, "socialProof.template", e.target.value)
                  }
                />
                <div className="form-text">
                  {t("formDefaults.templateHint")}
                </div>
              </Form.Group>
              <Row className="g-2 mb-3">
                <Col xs={6}>
                  <Form.Label className="small">
                    {t("formDefaults.minCount")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={f.socialProof.minCount}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setField(key, "socialProof.minCount", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label className="small">
                    {t("formDefaults.windowDays")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={30}
                    value={f.socialProof.windowDays}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setField(key, "socialProof.windowDays", e.target.value)
                    }
                  />
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Check
                  type="switch"
                  id={`${key}-googleRating`}
                  label={t("formDefaults.googleRating")}
                  checked={Boolean(f.googleRating.enabled)}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setField(key, "googleRating.enabled", e.target.checked)
                  }
                />
              </Form.Group>
              <Row className="g-2 mb-3">
                <Col xs={6}>
                  <Form.Label className="small">
                    {t("formDefaults.minRating")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step={0.1}
                    min={1}
                    max={5}
                    value={f.googleRating.minRating}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setField(key, "googleRating.minRating", e.target.value)
                    }
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label className="small">
                    {t("formDefaults.minReviews")}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    value={f.googleRating.minCount}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setField(key, "googleRating.minCount", e.target.value)
                    }
                  />
                </Col>
              </Row>
              {canEdit && (
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() => save(key)}
                >
                  {t("common.save", { defaultValue: "Speichern" })}
                </Button>
              )}
            </Col>
            <Col md={5}>
              <VariantPreview
                element={null}
                value={null}
                funnelType={funnelType}
                colours={{
                  submitButtonBackground:
                    f.submitButtonBackground ||
                    HARD_CODED.submitButtonBackground,
                  formHeaderBackground:
                    f.formHeaderBackground || HARD_CODED.formHeaderBackground
                }}
                socialProof={
                  f.socialProof.enabled
                    ? {
                        label: (f.socialProof.template || "")
                          .replace("{{count}}", "212")
                          .replace("{{category}}", "Badsanierung")
                      }
                    : null
                }
                googleRating={
                  f.googleRating.enabled ? { rating: 4.8, count: 37 } : null
                }
              />
            </Col>
          </Row>
        </CustomCard>
      </Col>
    );
  };

  return (
    <div className="pb-4">
      <PageHeading>{t("formDefaults.title")}</PageHeading>
      <div className="text-muted mb-3 ms-1">{t("formDefaults.intro")}</div>
      {isLoading ? (
        <CustomCard className="p-3">
          <table className="w-100">
            <tbody>
              <TableLoader colSpan={1} />
            </tbody>
          </table>
        </CustomCard>
      ) : (
        <Row className="g-3">
          {renderBlock("lead", t("splitTests.tabs.lead"))}
          {renderBlock("employee", t("splitTests.tabs.employee"))}
        </Row>
      )}
    </div>
  );
};

export default FormDefaults;
